import { setCommunityId } from "api/config";
import { useAppDispatch } from "app/hooks";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { communityActions, selectCommunity, selectCommunityLoading } from "redux/reducers/community";
import Loader from "./Loader";

interface CommunityContextType {
  communityName: string | null;
  communityConfig: any;
  loading: boolean;
  error: string | null;
  texts: Record<string, any>;
  assets: Record<string, any>;
  meta: Record<string, any>;
  theme: Record<string, any>;
}

const CommunityContext = createContext<CommunityContextType | undefined>(undefined);

export const CommunityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const communityID = process.env.REACT_APP_COMMUNITY_ID;

  const community = useSelector(selectCommunity);
  const communityLoading = useSelector(selectCommunityLoading);
  const dispatch = useAppDispatch();
  const [communityConfig, setCommunityConfig] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (!communityID) {
      setError("Community is not specified");
      setLoading(false);
      return;
    }
    dispatch(communityActions.fetchCommunity(communityID));
  }, [communityID])
  
  useEffect(() => {
    if (!community?.key || !communityID) {
      setError("Community is not specified");
      setLoading(false);
      return;
    }
    const loadConfig = async () => {
      try {
        setLoading(true);
        const module = await import(`../config/${community.key}`);
        setCommunityConfig(module.default);
        if (module.default.theme) {
          const { primary, secondary, error, success, grayDark, text, buttonBg, buttonText, buttonBorderRadius, fontElegant, fontBody, fontBold } = module.default.theme;
          document.documentElement.style.setProperty("--color-primary", primary);
          document.documentElement.style.setProperty("--color-secondary", secondary);
          document.documentElement.style.setProperty("--color-error", error);
          document.documentElement.style.setProperty("--color-success", success);
          document.documentElement.style.setProperty("--color-gray-dark", grayDark);
          document.documentElement.style.setProperty("--color-text", text);
          document.documentElement.style.setProperty("--color-button-bg", buttonBg);
          document.documentElement.style.setProperty("--color-button-text", buttonText);
          document.documentElement.style.setProperty("--button-border-radius", buttonBorderRadius);
          document.documentElement.style.setProperty("--font-elegant", fontElegant);
          document.documentElement.style.setProperty("--font-body", fontBody);
          document.documentElement.style.setProperty("--font-bold", fontBold);
        }
        setLoading(false);
        setError(null);
        setCommunityId(communityID)
      } catch (err) {
        setError(`Failed to load config for community: ${community}`);
        setLoading(false);
      }
    };

    loadConfig();
  }, [community?.key]);

  if(communityLoading || loading){
    return <Loader />
  }

  return (
    <CommunityContext.Provider value={{ communityName: community, communityConfig, loading, error, texts: communityConfig?.texts || {}, assets: communityConfig?.assets || {}, meta: communityConfig?.meta || {}, theme: communityConfig?.theme || {} }}>
      {children}
    </CommunityContext.Provider>
  );
};

export const useCommunity = (): CommunityContextType => {
  const context = useContext(CommunityContext);
  if (!context) {
    throw new Error("useCommunity must be used within a CommunityProvider");
  }
  return context;
};